
import {effect, Injectable, Signal, signal, WritableSignal} from '@angular/core';
import { Subject, BehaviorSubject, Observable, finalize, of, tap, concatMap } from 'rxjs';
import { ReplaySubject } from 'rxjs';

type StoragePayload<T> = {
  value: T;
  expiresAt?: number; // epoch milliseconds
};

export function signalWithStorageTTL<T>(
  key: string,
  defaultValue: T,
  options?: {
    ttlMinutes?: number;
    serialize?: (value: T) => string;
    deserialize?: (raw: string) => T;
  }
): WritableSignal<T> & { reset: () => void } {
  const serialize = options?.serialize ?? JSON.stringify;
  const deserialize = options?.deserialize ?? JSON.parse;
  const ttlMs = (options?.ttlMinutes ?? 0) * 60 * 1000;

  let initialValue: T;

  try {
    const raw = localStorage.getItem(key);
    if (raw !== null) {
      const payload = deserialize(raw) as StoragePayload<T>;
      const now = Date.now();
      if (!payload.expiresAt || payload.expiresAt > now) {
        initialValue = payload.value;
      } else {
        // expired
        initialValue = defaultValue;
        localStorage.removeItem(key);
      }
    } else {
      initialValue = defaultValue;
    }
  } catch {
    initialValue = defaultValue;
  }

  const state = signal<T>(initialValue);
  const writable = state as WritableSignal<T>;

  effect(() => {
    const value = state();
    try {
      if (value === defaultValue) {
        localStorage.removeItem(key);
      } else {
        const expiresAt = ttlMs > 0 ? Date.now() + ttlMs : undefined;
        const payload: StoragePayload<T> = { value, expiresAt };
        localStorage.setItem(key, serialize(payload));
      }
    } catch {
      // silent fail
    }
  });

  return Object.assign(writable, {
    reset: () => writable.set(defaultValue),
  });
}

//@Injectable({ providedIn: 'root' })
export function signalWithStorage<T>(
  key: string,
  defaultValue: T,
  options?: {
    serialize?: (value: T) => string;
    deserialize?: (raw: string) => T;
  }
): WritableSignal<T> & { reset: () => void } {

  /*

  const mySignal = signalWithStorage('myKey', 'default');

  // Writable
  mySignal.set('new value');
  mySignal.reset();

  // Read-only usage
  const readonlySignal = mySignal.asReadonly();
   */



  const serialize = options?.serialize ?? JSON.stringify;
  const deserialize = options?.deserialize ?? JSON.parse;

  let initialValue: T;

  try {
    const raw = localStorage.getItem(key);
    initialValue = raw !== null ? deserialize(raw) : defaultValue;
  } catch {
    initialValue = defaultValue;
  }

  const state = signal<T>(initialValue);

  effect(() => {
    const value = state();
    try {
      if (value === defaultValue) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, serialize(value));
      }
    } catch {
      // Ignore storage errors (e.g., quota exceeded, private mode)
    }
  });

  const writable = state as WritableSignal<T>;

  return Object.assign(writable, {
    reset: () => writable.set(defaultValue)
  });
}

@Injectable({ providedIn: 'root' })
export class CurrentCampaignGroupSelectSignalServiceTTL {

  /*
  const campaignId = signalWithStorageTTL<string>('campaignClientId', '', {
    ttlMinutes: 30  // expires in 30 minutes
  });
   */
  private currentClientId = signalWithStorageTTL('campaignClientId', '', {
    ttlMinutes: 60, // auto-expire after 1 hour
  });

  readonly clientId$ = this.currentClientId.asReadonly();

  announceCampaignGroupSelect(id: string) {
    this.currentClientId.set(id);
  }

  reset() {
    this.currentClientId.reset();
  }
}

@Injectable({ providedIn: 'root' })
export class CurrentCampaignGroupGenericSignalService {

  /*

  const signal1 = signalWithStorage<string>('name', 'Anonymous');
  signal1.set('David');
  console.log(signal1()); // "David"

  const signal2 = signalWithStorage<{ id: number; name: string }>('user', { id: 0, name: '' });
  signal2.set({ id: 123, name: 'CatsOf' });
  console.log(signal2()); // { id: 123, name: 'CatsOf' }

  const signal = signalWithStorage('compressed', [1, 2, 3], {
    serialize: (v) => btoa(JSON.stringify(v)),
    deserialize: (raw) => JSON.parse(atob(raw)),
  });
   */


  private currentClientId = signalWithStorage<string>('campaignClientId', '');
  readonly clientId$ = this.currentClientId.asReadonly();

  announceCampaignGroupSelect(id: string) {
    this.currentClientId.set(id);
  }

  reset() {
    this.currentClientId.reset();
  }
}




@Injectable({ providedIn: 'root' })
export class CurrentCampaignGroupSelectSignalService {

  /*
    // Example usage in a component:
    campaignService = inject(CurrentCampaignGroupSelectSignalService);

    // SET THE CURRENT CLIENT ID
    this.campaignService.announceCampaignGroupSelect(row.name);

    // Access the current client ID
    const currentId = this.campaignService.clientId$(); // 👈 read the current value
    console.log('Currently selected campaign group:', currentId);

    // HTML Template Example
    <p>Selected: {{ campaignService.clientId$() }}</p>
    OR
    @if (campaignService.clientId$() as id) {
    <p>📢 Selected Campaign Group: {{ id }}</p>
    }

    // Reset the current client ID
    this.campaignService.reset();

   */

  private currentClientId = signal<string>(localStorage.getItem('campaignClientId') || '');
  readonly clientId$ = this.currentClientId.asReadonly();

  constructor() {
    effect(() => {
      const id = this.currentClientId();
      if (id) {
        localStorage.setItem('campaignClientId', id);
      }
    });
  }

  announceCampaignGroupSelect(client_id: string) {
    this.currentClientId.set(client_id);
  }

  reset() {
    this.currentClientId.set('');
    localStorage.removeItem('campaignClientId');
  }
}


@Injectable({ providedIn: 'root' })
export class StorageService {

  private localStorageAvailableSubject = new BehaviorSubject<boolean>(true);
  localStorageAvailable$ = this.localStorageAvailableSubject.asObservable();

  constructor() {
    this.checkLocalStorage();
  }

  private checkLocalStorage() {
    const testKey = '__storage_test__';
    try {
      localStorage.setItem(testKey, testKey);
      if (localStorage.getItem(testKey) !== testKey) {
        throw new Error('localStorage mismatch');
      }
      localStorage.removeItem(testKey);
      this.localStorageAvailableSubject.next(true);
    } catch (e) {
      this.localStorageAvailableSubject.next(false);
    }
  }
}

@Injectable({ providedIn: 'root' })
export class CurrentLoggedInBehaviorService {

  constructor() {
  }

  private currentLoggedInAnnouncedBehaviorSource = new BehaviorSubject<boolean>(false);
  // Observable string streams
  currentLoggedInBehaviorAnnounced$ = this.currentLoggedInAnnouncedBehaviorSource.asObservable();

  // Service message commands
  announceCurrentLoggedInBehavior(current: boolean) {
    this.currentLoggedInAnnouncedBehaviorSource.next(current);
  }
}

@Injectable({ providedIn: 'root' })
export class CurrentSelectedSlugBehaviorService {

  constructor() {
  }

  private currentSlugAnnouncedBehaviorSource = new BehaviorSubject<string>('');
  // Observable string streams
  currentSlugBehaviorAnnounced$ = this.currentSlugAnnouncedBehaviorSource.asObservable();

  // Service message commands
  announceCurrentSlugBehavior(current: string) {
    this.currentSlugAnnouncedBehaviorSource.next(current);
  }
}


@Injectable({ providedIn: 'root' })
export class CurrentCrudSelectBehaviorServiceAsString {

  constructor() { }

  private current = new BehaviorSubject<string>('');
  current$ = this.current.asObservable();
  announce(client_id: string) {
    this.current.next(client_id);
  }
}











@Injectable({ providedIn: 'root' })
export class CurrentSelectedClientIdBehaviorService {

  constructor() {
  }

  private currentClientIdAnnouncedBehaviorSource = new BehaviorSubject<number>(0);
  // Observable string streams
  currentClientIdBehaviorAnnounced$ = this.currentClientIdAnnouncedBehaviorSource.asObservable();

  // Service message commands
  announceCurrentClientIdBehavior(current: number) {
    this.currentClientIdAnnouncedBehaviorSource.next(current);
  }
}



@Injectable({ providedIn: 'root' })
export class SuperAdminViewBehaviorService {

  private readonly superAdminViewKey = 'superadmin_view'; // Key for sessionStorage
  private currentSuperAdminViewAnnouncedBehaviorSource: BehaviorSubject<boolean>;

  // Observable string stream
  currentSuperAdminViewBehaviorAnnounced$!: Observable<boolean>;

  constructor() {
    const storedValue = sessionStorage.getItem(this.superAdminViewKey);
    const initialValue = storedValue !== null ? JSON.parse(storedValue) : false;

    this.currentSuperAdminViewAnnouncedBehaviorSource = new BehaviorSubject<boolean>(initialValue);
    this.currentSuperAdminViewBehaviorAnnounced$ = this.currentSuperAdminViewAnnouncedBehaviorSource.asObservable();
  }

  // Service message commands
  announceSuperAdminViewBehavior(current: boolean) {
    sessionStorage.setItem(this.superAdminViewKey, JSON.stringify(current));
    this.currentSuperAdminViewAnnouncedBehaviorSource.next(current);
  }
}



@Injectable({ providedIn: 'root' })
export class CurrentRefreshTokenService {

  constructor() { }

  private currentRefreshTokenAnnouncedSource = new Subject<boolean>();
  // Observable string streams
  currentRefreshTokenAnnounced$ = this.currentRefreshTokenAnnouncedSource.asObservable();
  // Service message commands
  announceRefreshToken(is_logged_in: boolean) {
    this.currentRefreshTokenAnnouncedSource.next(is_logged_in);
  }

}


@Injectable({ providedIn: 'root' })
export class SnackBarService {

  constructor() { }

  private snackbarAnnouncedSource = new Subject<string>();
  // Observable string streams
  snackbarAnnounced$ = this.snackbarAnnouncedSource.asObservable();
  // Service message commands
  announceSackBar(snackbar: string) {
    this.snackbarAnnouncedSource.next(snackbar);
  }

}







@Injectable({ providedIn: 'root' })
export class LoadingService {

  /*
  https://www.youtube.com/watch?v=czf4Bh8eNPA
  concantMap is super cool
   */

  private loadingSubject = new BehaviorSubject<boolean>(false);

  loading$: Observable<boolean> = this.loadingSubject.asObservable();

  showLoadingUntilCompleted<T>(obs$: Observable<T>): Observable<T> {  // <T> is a generic type
    return of(null).pipe(
      tap(() => this.loadingOn()),
      concatMap(() => obs$),
      finalize(() => this.loadingOff())

    )
  }

  loadingOn() {
    this.loadingSubject.next(true);

  }

  loadingOff() {
    this.loadingSubject.next(false);

  }

} // end LoadingService


@Injectable({ providedIn: 'root' })
export class LoadingMessagesService {

  private loadingSubject = new BehaviorSubject<boolean>(false);

  loading$: Observable<boolean> = this.loadingSubject.asObservable();

  showLoadingUntilCompleted<T>(obs$: Observable<T>): Observable<T> {  // <T> is a generic type
    return of(null).pipe(
      tap(() => this.loadingOn()),
      concatMap(() => obs$),
      finalize(() => this.loadingOff())

    )
  }

  loadingOn() {
    this.loadingSubject.next(true);

  }

  loadingOff() {
    this.loadingSubject.next(false);

  }

} // end LoadingService


@Injectable({ providedIn: 'root' })
export class LoadingCalendarService {

  private loadingSubject = new BehaviorSubject<boolean>(false);

  loading$: Observable<boolean> = this.loadingSubject.asObservable();

  showLoadingUntilCompleted<T>(obs$: Observable<T>): Observable<T> {  // <T> is a generic type
    return of(null).pipe(
      tap(() => this.loadingOn()),
      concatMap(() => obs$),
      finalize(() => this.loadingOff())

    )
  }

  loadingOn() {
    this.loadingSubject.next(true);

  }

  loadingOff() {
    this.loadingSubject.next(false);

  }

} // end LoadingService


@Injectable({ providedIn: 'root' })
export class CurrentClientIdSelectService {

  constructor() { }

  private currentClientIdSelectAnnouncedSource = new BehaviorSubject<string>('');
  // Observable string streams
  currentClientIdSelectAnnounced$ = this.currentClientIdSelectAnnouncedSource.asObservable();
  // Service message commands
  announceClientIdSelect(client_id: string) {
    this.currentClientIdSelectAnnouncedSource.next(client_id);
  }
}

@Injectable({ providedIn: 'root' })
export class CurrentClientSlugSelectService {

  constructor() { }

  private currentClientSlugSelectAnnouncedSource = new BehaviorSubject<string>('');
  // Observable string streams
  currentClientSlugSelectAnnounced$ = this.currentClientSlugSelectAnnouncedSource.asObservable();
  // Service message commands
  announceClientSlugSelect(client_id: string) {
    this.currentClientSlugSelectAnnouncedSource.next(client_id);
  }
}


/*
* ****************CAMPAIGN RELATED SERVICES****************
*
* */

export function createDefault<T>(template: T): T {

  // const defaultGetAllAd: GetAllAdPayloadModel = createDefault(getAllAdTemplate);

  const result: any = Array.isArray(template) ? [] : {};

  for (const key in template) {
    if (!Object.prototype.hasOwnProperty.call(template, key)) continue;

    const value = (template as any)[key];

    if (typeof value === 'string') {
      result[key] = '';
    } else if (typeof value === 'number') {
      result[key] = 0;
    } else if (typeof value === 'boolean') {
      result[key] = false;
    } else if (Array.isArray(value)) {
      result[key] = [];
    } else if (value !== null && typeof value === 'object') {
      result[key] = createDefault(value);
    } else {
      result[key] = null;
    }
  }

  return result as T;
}



@Injectable({ providedIn: 'root' })
export class CurrentCrudSelectService<T> {
  /*
  To create a generic we have to use a subject else we cn a gracefully scale
  the intialization of the service becuase we can provide defaulkt values of object
  to scale a object then use string and decode
   */
  private currentCrudSelectAnnouncedSource = new Subject<T>();
  currentCrudSelectAnnounced$: Observable<T> = this.currentCrudSelectAnnouncedSource.asObservable();

  announceCrudSelect(crud_object: T) {
    this.currentCrudSelectAnnouncedSource.next(crud_object);
  }
}







// @Injectable({ providedIn: 'root' })
// export class CurrentCrudSelectBehaviorServiceAsString {
//
//   private currentCrudSelectAnnouncedSource = new BehaviorSubject<string>('');
//   currentCrudSelectAnnounced$: Observable<string> = this.currentCrudSelectAnnouncedSource.asObservable();
//
//   announceCrudSelect(value: string): void {
//     this.currentCrudSelectAnnouncedSource.next(value);
//   }
// }


















@Injectable({ providedIn: 'root' })
export class CurrentCrudSelectServiceReplay<T> {
  private subject = new ReplaySubject<T>(1);
  current$: Observable<T> = this.subject.asObservable();

  announce(value: T) {
    this.subject.next(value);
  }
}


@Injectable({ providedIn: 'root' })
export class CurrentCampaignGroupSequenceSelectService {

  constructor() { }

  private currentCampaignGroupSequenceSelectAnnouncedSource = new BehaviorSubject<string>('');
  // Observable string streams
  currentCampaignGroupSequenceSelectAnnounced$ = this.currentCampaignGroupSequenceSelectAnnouncedSource.asObservable();
  // Service message commands
  announceCampaignGroupSequenceSelect(client_id: string) {
    this.currentCampaignGroupSequenceSelectAnnouncedSource.next(client_id);
  }
}


@Injectable({ providedIn: 'root' })
export class CurrentCampaignGroupSelectService {

  constructor() { }

  private currentCampaignGroupSelectAnnouncedSource = new BehaviorSubject<string>('');
  // Observable string streams
  currentCampaignGroupSelectAnnounced$ = this.currentCampaignGroupSelectAnnouncedSource.asObservable();
  // Service message commands
  announceCampaignGroupSelect(client_id: string) {
    this.currentCampaignGroupSelectAnnouncedSource.next(client_id);
  }
}

@Injectable({ providedIn: 'root' })
export class CurrentCampaignSelectService {

  constructor() { }

  private currentCampaignSelectAnnouncedSource = new BehaviorSubject<string>('');
  // Observable string streams
  currentCampaignSelectAnnounced$ = this.currentCampaignSelectAnnouncedSource.asObservable();
  // Service message commands
  announceCampaignSelect(client_id: string) {
    this.currentCampaignSelectAnnouncedSource.next(client_id);
  }
}

@Injectable({ providedIn: 'root' })
export class CurrentAdgroupSelectService {

  constructor() { }

  private currentAdgroupSelectAnnouncedSource = new BehaviorSubject<string>('');
  // Observable string streams
  currentAdgroupSelectAnnounced$ = this.currentAdgroupSelectAnnouncedSource.asObservable();
  // Service message commands
  announceAdgroupSelect(client_id: string) {
    this.currentAdgroupSelectAnnouncedSource.next(client_id);
  }
}


@Injectable({ providedIn: 'root' })
export class CurrentAdSelectService {

  constructor() { }

  private currentAdSelectAnnouncedSource = new BehaviorSubject<string>('');
  // Observable string streams
  currentAdSelectAnnounced$ = this.currentAdSelectAnnouncedSource.asObservable();
  // Service message commands
  announceAdSelect(client_id: string) {
    this.currentAdSelectAnnouncedSource.next(client_id);
  }
}









